import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Scissors } from "lucide-react";
import { API } from "@/lib/api";
import { ModClipAlert } from "@/components/ModClipAlert";
import { OverlayHoliday } from "@/components/holiday/OverlayHoliday";

const useTransparent = () => useEffect(() => {
  const els = [document.documentElement, document.body, document.getElementById("root")];
  els.forEach((el) => el && el.style.setProperty("background", "transparent", "important"));
  document.body.classList.add("cx-overlay-mode");
}, []);

export default function Overlay() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const alertOn = params.get("alert") === "1";
  const modOn = alertOn || params.get("mod") === "1";
  const demo = params.get("demo") === "1";
  const soundOff = params.get("sound") === "0";
  const vol = params.get("vol");
  const [alert, setAlert] = useState(null);
  const [mod, setMod] = useState(null);
  const seen = useRef(null);
  const modSeen = useRef(undefined);
  useTransparent();

  useEffect(() => {
    if (!alertOn && !modOn) return undefined;
    const snip = soundOff ? null : new Audio("/sfx/snip.mp3");
    if (snip) snip.volume = Math.max(0, Math.min(1, Number(vol ?? 80) / 100));
    const showMod = (m) => {
      setMod(null); setTimeout(() => setMod(m), 30); setTimeout(() => setMod((x) => (x === m ? null : x)), 5700);
      if (snip) { snip.currentTime = 0; snip.play().catch(() => {}); }
    };
    if (demo) {
      const m = () => showMod({ by: "TonModo", secs: 30, at: Date.now() });
      m();
      const di = setInterval(m, 8000);
      return () => clearInterval(di);
    }
    const load = () => fetch(`${API}/overlay/${key}/feed`).then((r) => r.json()).then((d) => {
      if (modOn) {
        const at = d.modclip?.at || null;
        if (modSeen.current !== undefined && at && at !== modSeen.current) showMod(d.modclip);
        modSeen.current = at;
      }
      if (!alertOn) return;
      const done = (d.events || []).filter((e) => e.type === "clip");
      if (seen.current === null) { seen.current = new Set(done.map((e) => e.id)); return; }
      const fresh = done.find((e) => !seen.current.has(e.id));
      done.forEach((e) => seen.current.add(e.id));
      if (fresh) { setAlert(fresh); setTimeout(() => setAlert(null), 3000); }
    }).catch(() => {});
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, [key, alertOn, modOn, demo, soundOff, vol]);

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ background: "transparent" }} data-testid="overlay-page" data-alert={alertOn ? "on" : "off"} data-mod={modOn ? "on" : "off"}>
      {mod && <ModClipAlert a={mod} en={params.get("lang") === "en"} />}
      {params.get("holiday") !== "0" && <OverlayHoliday overlayKey={key} preview={params.get("holiday_preview")} />}
      {alert && (
        <div className="absolute bottom-6 left-6 cx-rise flex items-center gap-2 rounded-full px-3 py-1.5 bg-black/45 backdrop-blur-md border border-white/10 text-white/85 text-xs font-semibold" data-testid="overlay-clip-alert">
          <Scissors className="w-3.5 h-3.5 text-[#3ad7ff]" /> Clip !
          {alert.score != null && <span className="font-mono text-[#3ad7ff]">{alert.score}</span>}
        </div>
      )}
    </div>
  );
}
